// Front-end for a Help article (Pages/Help/Template.cshtml). Two jobs:
//   1. Header search icon → opens the shared full-screen Help search overlay
//      (identical to the Help index's mobile search). The icon keeps href="/help"
//      as a no-JS fallback; initHelpSearchOverlay binds the trigger + preventDefault.
//   2. "Did this article help?" voting — likes / dislikes. The reader's own current
//      vote is stored locally (localStorage, keyed vote:<slug>); each click POSTs the
//      transition (from → to) to WateWebApi, which atomically adjusts the document's
//      aggregate /likes /dislikes counters. Clicking the active vote cancels it;
//      clicking the other switches.
import axios from 'axios';
import { initHelpSearchOverlay } from '../../Assets/helpSearch';

type VoteDir = 'like' | 'dislike';

// ── Header search icon → overlay ─────────────────────────────────────────────
// initHelpSearchOverlay binds the shared [data-help-search] header icon itself.
initHelpSearchOverlay();

// ── Article feedback voting ──────────────────────────────────────────────────
const feedback = document.querySelector<HTMLElement>('.article-feedback');
const slug = feedback?.dataset.slug;

if (feedback && slug) {
    const buttons = Array.from(feedback.querySelectorAll<HTMLButtonElement>('.vote'));

    const paint = (current: VoteDir | undefined): void => {
        for (const button of buttons) {
            const on = button.dataset.vote === current;
            button.classList.toggle('active', on);
            button.setAttribute('aria-pressed', String(on));
        }
    };

    const key = `vote:${slug}`;
    const readVote = (): VoteDir | null => {
        const v = localStorage.getItem(key);
        return v === 'like' || v === 'dislike' ? v : null;
    };

    // Reflect any previously stored vote on load.
    paint(readVote() ?? undefined);

    const vote = async (clicked: VoteDir): Promise<void> => {
        const from = readVote();                      // vote being removed
        const to = from === clicked ? null : clicked; // same button cancels; else switch/new

        // Optimistic local update + UI; revert if the server rejects.
        if (to) localStorage.setItem(key, to);
        else localStorage.removeItem(key);
        paint(to ?? undefined);

        try {
            // /Powerush is WateWebApi's controller route, not branding — see Pages/App.cshtml.cs.
            await axios.post(`${window.webApiUrl}/Powerush/help/${slug}/vote`, { from, to });
        } catch {
            if (from) localStorage.setItem(key, from);
            else localStorage.removeItem(key);
            paint(from ?? undefined);
        }
    };

    feedback.addEventListener('click', (event) => {
        const clicked = (event.target as Element).closest<HTMLButtonElement>('.vote')?.dataset.vote;
        if (clicked === 'like' || clicked === 'dislike') void vote(clicked);
    });
}
